import { Text } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import { type DatabaseFieldType } from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { isQuickCalcDisabledFieldTypes } from './quickCalcUtils';

interface DatabaseCalculationLabelProps {
  type?: DatabaseCalculationType;
  fieldType?: DatabaseFieldType;
  useBoldText?: boolean;
  hasCompatibleCalculation?: boolean;
}

export const DatabaseCalculationLabel: React.FC<DatabaseCalculationLabelProps> = ({
  type,
  fieldType,
  useBoldText = false,
  hasCompatibleCalculation,
}) => {
  const { formatMessage } = useIntl();

  const labelMap: Record<DatabaseCalculationType, string> = {
    [DatabaseCalculationType.SUM]: formatMessage(
      messages['quick-calculations.calculation-type-sum']
    ),
    [DatabaseCalculationType.COUNT_TOTAL]: formatMessage(
      messages['quick-calculations.calculation-type-count-total']
    ),
    [DatabaseCalculationType.COUNT]: formatMessage(
      messages['quick-calculations.calculation-type-count']
    ),
    [DatabaseCalculationType.COUNT_EMPTY]: formatMessage(
      messages['quick-calculations.calculation-type-count-empty']
    ),
    [DatabaseCalculationType.COUNT_UNIQUE]: formatMessage(
      messages['quick-calculations.calculation-type-count-unique']
    ),
    [DatabaseCalculationType.PERCENT_EMPTY]: formatMessage(
      messages['quick-calculations.calculation-type-percent-empty']
    ),
    [DatabaseCalculationType.PERCENT_NOT_EMPTY]: formatMessage(
      messages['quick-calculations.calculation-type-percent-not-empty']
    ),
    [DatabaseCalculationType.AVERAGE]: formatMessage(
      messages['quick-calculations.calculation-type-average']
    ),
    [DatabaseCalculationType.MIN]: formatMessage(
      messages['quick-calculations.calculation-type-min']
    ),
    [DatabaseCalculationType.MAX]: formatMessage(
      messages['quick-calculations.calculation-type-max']
    ),
  };

  const isDisabled = fieldType && isQuickCalcDisabledFieldTypes(fieldType);

  return (
    <Text
      color={
        isDisabled
          ? 'color.text.disabled'
          : useBoldText
          ? 'color.text.subtlest'
          : 'color.text'
      }
      weight={useBoldText ? 'semibold' : 'regular'}
    >
      {type !== undefined && hasCompatibleCalculation !== false
        ? labelMap[type]
        : formatMessage(messages['quick.calculations.placeholder-text'])}
    </Text>
  );
};
